import React, { useEffect, useState } from 'react';

interface Component {
  name: string;
  status: string;
  description: string;
  lastRecording?: string | null;
}

interface UptimeEntry {
  hour?: string;
  date?: string;
  pct: number | null;
  checks: number;
}

interface Incident {
  component: string;
  title: string;
  status: string;
  impact: string;
  message: string;
  createdAt: string;
  resolvedAt: string | null;
  durationMinutes: number | null;
}

interface StatusData {
  updatedAt: string;
  overall: string;
  components: Component[];
  uptime: Record<string, UptimeEntry[]>;
  incidents: Incident[];
}

const STATUS_LABELS: Record<string, string> = {
  operational: 'Operational',
  degraded: 'Degraded Performance',
  partial_outage: 'Partial Outage',
  major_outage: 'Major Outage',
  no_data: 'No Data Yet',
  unknown: 'Unknown',
};

const STATUS_COLORS: Record<string, string> = {
  operational: '#22c55e',
  degraded: '#f59e0b',
  partial_outage: '#f97316',
  major_outage: '#ef4444',
  no_data: '#94a3b8',
  unknown: '#94a3b8',
};

const OVERALL_BG: Record<string, string> = {
  operational: '#22c55e',
  degraded: '#f59e0b',
  partial_outage: '#f97316',
  major_outage: '#ef4444',
};

function StatusPage() {
  const [data, setData] = useState<StatusData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStatus = () => {
      fetch('/status.json?' + Date.now())
        .then(r => {
          if (!r.ok) throw new Error(`HTTP ${r.status}`);
          return r.json();
        })
        .then(d => { setData(d); setError(null); setLoading(false); })
        .catch(e => { setError(e.message); setLoading(false); });
    };
    fetchStatus();
    const interval = setInterval(fetchStatus, 60000);
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <div style={styles.page}>
        <div style={styles.container}>
          <div style={{ textAlign: 'center', padding: 80, color: '#64748b' }}>Loading status...</div>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div style={styles.page}>
        <div style={styles.container}>
          <Header />
          <div style={{ ...styles.overallBanner, background: '#94a3b8' }}>
            <span style={{ fontSize: 20 }}>Unable to load status data</span>
          </div>
          <div style={{ textAlign: 'center', padding: 40, color: '#64748b' }}>
            {error || 'No status data available yet. The health monitor will publish data shortly.'}
          </div>
        </div>
      </div>
    );
  }

  const overallColor = OVERALL_BG[data.overall] || '#94a3b8';
  const ongoingIncidents = data.incidents.filter(i => i.status === 'ongoing');
  const pastIncidents = data.incidents.filter(i => i.status === 'resolved');

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        <Header />

        {/* Overall status banner */}
        <div style={{ ...styles.overallBanner, background: overallColor }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 12, height: 12, borderRadius: '50%', background: '#fff' }} />
            <span style={{ fontSize: 20, fontWeight: 600 }}>
              {data.overall === 'operational' ? 'All Systems Operational' :
               data.overall === 'partial_outage' ? 'Partial System Outage' :
               data.overall === 'major_outage' ? 'Major System Outage' :
               'Degraded Performance'}
            </span>
          </div>
          <span style={{ fontSize: 13, opacity: 0.85 }}>
            Updated {formatRelativeTime(data.updatedAt)}
          </span>
        </div>

        {/* Active incidents */}
        {ongoingIncidents.length > 0 && (
          <div style={styles.section}>
            <div style={{ ...styles.card, borderLeft: '4px solid #ef4444' }}>
              {ongoingIncidents.map((inc, i) => (
                <div key={i} style={{ padding: '16px 0', borderBottom: i < ongoingIncidents.length - 1 ? '1px solid #f1f5f9' : 'none' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    <span style={{ ...styles.impactBadge, background: inc.impact === 'critical' ? '#fef2f2' : '#fff7ed', color: inc.impact === 'critical' ? '#dc2626' : '#ea580c' }}>
                      {inc.impact}
                    </span>
                    <span style={{ fontWeight: 600, fontSize: 15 }}>{inc.title}</span>
                  </div>
                  <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: 13 }}>
                    {inc.message} — started {formatRelativeTime(inc.createdAt)}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Component statuses */}
        <div style={styles.section}>
          <div style={styles.card}>
            {data.components.map((comp, i) => (
              <div key={comp.name} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '14px 0',
                borderBottom: i < data.components.length - 1 ? '1px solid #f1f5f9' : 'none',
              }}>
                <div>
                  <div style={{ fontWeight: 500, fontSize: 14, color: '#1e293b' }}>{comp.name}</div>
                  <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>{comp.description}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 13, color: STATUS_COLORS[comp.status] || '#94a3b8', fontWeight: 500 }}>
                    {STATUS_LABELS[comp.status] || comp.status}
                  </span>
                  <div style={{
                    width: 10, height: 10, borderRadius: '50%',
                    background: STATUS_COLORS[comp.status] || '#94a3b8',
                  }} />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Uptime charts */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Uptime — Last 7 Days</h2>
          {Object.entries(data.uptime).map(([key, entries]) => (
            <UptimeChart key={key} label={key === 'nas-stream' ? 'NAS Stream Server' : 'Audio Playback'} entries={entries} />
          ))}
        </div>

        {/* Past incidents */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Past Incidents</h2>
          {pastIncidents.length === 0 ? (
            <div style={{ ...styles.card, color: '#64748b', textAlign: 'center', padding: 32 }}>
              No incidents recorded
            </div>
          ) : (
            <div style={styles.card}>
              {pastIncidents.map((inc, i) => (
                <div key={i} style={{
                  padding: '14px 0',
                  borderBottom: i < pastIncidents.length - 1 ? '1px solid #f1f5f9' : 'none',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                        <span style={{ ...styles.impactBadge, background: '#f0fdf4', color: '#16a34a' }}>resolved</span>
                        <span style={{ fontWeight: 500, fontSize: 14 }}>{inc.title}</span>
                      </div>
                      <p style={{ margin: 0, color: '#64748b', fontSize: 13 }}>{inc.message}</p>
                    </div>
                    <div style={{ textAlign: 'right', whiteSpace: 'nowrap', marginLeft: 16 }}>
                      <div style={{ fontSize: 12, color: '#94a3b8' }}>{formatDate(inc.createdAt)}</div>
                      {inc.durationMinutes != null && (
                        <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                          Duration: {formatDuration(inc.durationMinutes)}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <Footer />
      </div>
    </div>
  );
}

function Header() {
  return (
    <div style={styles.header}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ width: 32, height: 32, borderRadius: 8, background: '#00bcd4', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: 14 }}>PS</div>
        <div>
          <div style={{ fontWeight: 700, fontSize: 18, color: '#1e293b' }}>Pulse Storage</div>
          <div style={{ fontSize: 12, color: '#94a3b8' }}>System Status</div>
        </div>
      </div>
      <a href="/" style={{ fontSize: 13, color: '#3b82f6', textDecoration: 'none' }}>
        Go to Dashboard
      </a>
    </div>
  );
}

function Footer() {
  return (
    <div style={{ textAlign: 'center', padding: '32px 0', color: '#94a3b8', fontSize: 12 }}>
      <p>Pulse Storage — Woxom Health Call Recording Archive</p>
      <p>Status data updated every 15 minutes</p>
    </div>
  );
}

function UptimeChart({ label, entries }: { label: string; entries: UptimeEntry[] }) {
  // Calculate overall uptime percentage
  const withData = entries.filter(d => d.pct !== null);
  const avgUptime = withData.length > 0
    ? (withData.reduce((sum, d) => sum + (d.pct || 0), 0) / withData.length).toFixed(2)
    : null;

  // Reversed so oldest is on the left
  const ordered = [...entries].reverse();

  // Find day boundaries for labels
  const dayLabels: { index: number; label: string }[] = [];
  let lastDay = '';
  ordered.forEach((entry, i) => {
    const key = entry.hour || entry.date || '';
    const day = key.slice(0, 10);
    if (day && day !== lastDay) {
      const d = new Date(day + 'T00:00:00');
      dayLabels.push({ index: i, label: d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) });
      lastDay = day;
    }
  });

  return (
    <div style={{ ...styles.card, marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <span style={{ fontWeight: 500, fontSize: 14, color: '#1e293b' }}>{label}</span>
        <span style={{ fontSize: 13, color: '#64748b' }}>
          {avgUptime !== null ? `${avgUptime}% uptime` : 'No data'}
        </span>
      </div>
      <div style={{ display: 'flex', gap: 0.5, alignItems: 'flex-end', height: 34 }}>
        {ordered.map((entry, i) => {
          const color = entry.pct === null ? '#e2e8f0'
            : entry.pct === 100 ? '#22c55e'
            : entry.pct >= 99 ? '#86efac'
            : entry.pct >= 95 ? '#f59e0b'
            : '#ef4444';
          const key = entry.hour || entry.date || '';
          const hourLabel = entry.hour ? `${key.slice(11, 13)}:00` : key;
          const dayLabel = key.slice(0, 10);
          return (
            <div
              key={i}
              title={`${dayLabel} ${hourLabel}: ${entry.pct !== null ? entry.pct + '%' : 'No data'} (${entry.checks} checks)`}
              style={{
                flex: 1,
                height: 34,
                borderRadius: 1,
                background: color,
                cursor: 'pointer',
                transition: 'opacity 0.15s',
              }}
              onMouseEnter={e => (e.currentTarget.style.opacity = '0.7')}
              onMouseLeave={e => (e.currentTarget.style.opacity = '1')}
            />
          );
        })}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, fontSize: 11, color: '#94a3b8' }}>
        {dayLabels.length > 1 ? (
          <>
            <span>{dayLabels[0]?.label}</span>
            {dayLabels.length > 2 && <span>{dayLabels[Math.floor(dayLabels.length / 2)]?.label}</span>}
            <span>{dayLabels[dayLabels.length - 1]?.label}</span>
          </>
        ) : (
          <>
            <span>7 days ago</span>
            <span>Now</span>
          </>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatRelativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function formatDuration(minutes: number): string {
  if (minutes < 60) return `${minutes}m`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const styles: Record<string, React.CSSProperties> = {
  page: {
    minHeight: '100vh',
    background: '#f8fafc',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  container: {
    maxWidth: 720,
    margin: '0 auto',
    padding: '0 20px',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '24px 0',
  },
  overallBanner: {
    borderRadius: 10,
    padding: '20px 24px',
    color: '#fff',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 24,
  },
  section: {
    marginBottom: 24,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: 600,
    color: '#1e293b',
    margin: '0 0 12px 0',
  },
  card: {
    background: '#fff',
    borderRadius: 10,
    padding: '4px 20px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)',
  },
  impactBadge: {
    fontSize: 11,
    fontWeight: 600,
    padding: '2px 8px',
    borderRadius: 4,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.03em',
  },
};

export default StatusPage;
